import axios from "axios";
import React, { useState } from "react";

import { api } from "../lib/api";
import { nameFormSchema } from "../schemas/settings";

interface NamedItem {
  id: number;
  name: string;
  is_active: boolean;
}

interface SimpleCrudSectionProps {
  title: string;
  apiPath: string;
  itemLabel: string;
  items: NamedItem[];
  allowDelete: boolean;
  onItemsChange: (items: NamedItem[]) => void;
}

interface FormState {
  mode: "create" | "edit";
  item?: NamedItem;
}

export function SimpleCrudSection({
  title,
  apiPath,
  itemLabel,
  items,
  allowDelete,
  onItemsChange,
}: SimpleCrudSectionProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openCreate = () => {
    setName("");
    setIsActive(true);
    setFieldError(null);
    setFormError(null);
    setForm({ mode: "create" });
  };

  const openEdit = (item: NamedItem) => {
    setName(item.name);
    setIsActive(item.is_active);
    setFieldError(null);
    setFormError(null);
    setForm({ mode: "edit", item });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const result = nameFormSchema.safeParse({ name });
    if (!result.success) {
      setFieldError(result.error.flatten().fieldErrors.name?.[0] ?? null);
      return;
    }
    setFieldError(null);

    setIsSubmitting(true);
    try {
      if (form?.mode === "edit" && form.item) {
        const response = await api.patch<NamedItem>(`${apiPath}${form.item.id}/`, {
          name: result.data.name,
          is_active: isActive,
        });
        onItemsChange(items.map((item) => (item.id === response.data.id ? response.data : item)));
      } else {
        const response = await api.post<NamedItem>(apiPath, {
          name: result.data.name,
          is_active: isActive,
        });
        onItemsChange([...items, response.data]);
      }
      setForm(null);
    } catch {
      setFormError(`Não foi possível salvar o ${itemLabel}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item: NamedItem) => {
    if (!window.confirm(`Excluir o ${itemLabel} "${item.name}"?`)) return;
    try {
      await api.delete(`${apiPath}${item.id}/`);
      onItemsChange(items.filter((existing) => existing.id !== item.id));
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
      window.alert(detail ?? `Não foi possível excluir o ${itemLabel}.`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
        >
          Novo {itemLabel}
        </button>
      </div>

      <div className="mt-3 overflow-x-auto rounded-[10px] border border-border-subtle">
        <table className="min-w-full divide-y divide-border-subtle text-sm">
          <thead className="bg-surface">
            <tr>
              {["Nome", "Ativo", ""].map((header) => (
                <th key={header} className="px-4 py-2 text-left font-medium text-slate-600">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-slate-800">{item.name}</td>
                <td className="px-4 py-2 text-slate-600">{item.is_active ? "Sim" : "Não"}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="mr-3 text-sm font-medium text-primary hover:underline"
                  >
                    Editar
                  </button>
                  {allowDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      className="text-sm font-medium text-red-600 hover:underline"
                    >
                      Excluir
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-sm rounded-[10px] border border-border-subtle bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800">
              {form.mode === "edit" ? `Editar ${itemLabel}` : `Novo ${itemLabel}`}
            </h3>
            <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="item_name" className="block text-sm font-medium text-slate-700">
                  Nome
                </label>
                <input
                  id="item_name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {fieldError && <p className="mt-1 text-sm text-red-600">{fieldError}</p>}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
                Ativo
              </label>

              {formError && <p className="text-sm text-red-600">{formError}</p>}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setForm(null)}
                  className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60"
                >
                  {isSubmitting ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
